/**
 * Invoice business-rule validation (RO_CIUS / UBL 2.1 subset)
 * ----------------------------------------------------------
 * Pure TypeScript — runs in the browser AND in Deno edge functions.
 * No imports from @/, no DOM, no Supabase. The edge function copies
 * this file via mirror at supabase/functions/_shared/invoiceValidation.ts.
 *
 * Goal: catch ~95% of ANAF rejections BEFORE upload, with messages in
 * Romanian that point to a specific field. The full XSD is intentionally
 * not validated here (see mem://features/checklist/legal.md #15 — XSD
 * complete is deferred until ANAF SPV connection).
 *
 * Field codes are stable identifiers (not labels). The UI maps them to
 * `data-invalid-field={code}` so clicking a banner row scrolls + focuses
 * the offending input.
 */
import { ROMANIAN_COUNTIES, UNIT_CODES } from "./romanianCounties";

// ---------- Types ----------

export type ValidationSeverity = "error" | "warning";

export interface ValidationIssue {
  /** Stable field id, e.g. "buyer.cui" or "items[2].unit_price" */
  field: string;
  /** Human-friendly label for the banner */
  fieldLabel: string;
  severity: ValidationSeverity;
  /** Stable error code for telemetry */
  code: string;
  /** Romanian error message shown to the user */
  message: string;
}

export interface InvoiceValidationInput {
  invoice_number: string | null;
  issue_date: string | null; // YYYY-MM-DD
  due_date: string | null;   // YYYY-MM-DD
  seller_name: string | null;
  seller_cui: string | null;
  seller_address: string | null;
  seller_city: string | null;
  seller_county: string | null;
  buyer_name: string | null;
  buyer_cui: string | null;
  buyer_address: string | null;
  buyer_city: string | null;
  buyer_county: string | null;
  subtotal?: number | null;
  vat_total?: number | null;
  total?: number | null;
  items: Array<{
    item_name: string;
    quantity: number;
    unit_price: number;
    vat_rate: number;
    unit_code: string;
  }>;
}

// ---------- Constants ----------

/** RO_CIUS allowed VAT rates (Cota TVA standard / redusă / zero) */
const ALLOWED_VAT_RATES = [0, 5, 9, 11, 19, 21] as const;

const COUNTY_CODES = new Set<string>(ROMANIAN_COUNTIES.map((c) => c.code));
const UNIT_CODE_SET = new Set<string>(UNIT_CODES.map((u) => u.code));

/** Reasonable cents-precision tolerance for floating-point sums */
const MONEY_EPSILON = 0.01;

// ---------- Helpers ----------

const isBlank = (v: string | null | undefined): boolean =>
  !v || v.trim().length === 0;

const isValidDate = (v: string | null | undefined): boolean => {
  if (!v) return false;
  // YYYY-MM-DD with valid calendar date
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
  const d = new Date(v + "T00:00:00Z");
  return !isNaN(d.getTime());
};

/** Inline copy of CUI/CNP validation so this file stays import-light for Deno. */
function validateCuiCnpInline(value: string): { valid: boolean; type: "cui" | "cnp" | "unknown" } {
  const trimmed = value.trim();
  if (!trimmed) return { valid: false, type: "unknown" };
  const stripped = trimmed.replace(/^RO/i, "").replace(/\s/g, "");
  if (!/^\d+$/.test(stripped)) return { valid: false, type: "unknown" };
  // Force CUI when RO prefix is present, even on 13 digits.
  if (/^RO/i.test(trimmed)) {
    if (stripped.length >= 1 && stripped.length <= 10) return { valid: true, type: "cui" };
    return { valid: false, type: "unknown" };
  }
  if (stripped.length === 13 && /^[1-8]/.test(stripped)) return { valid: true, type: "cnp" };
  if (stripped.length >= 1 && stripped.length <= 10) return { valid: true, type: "cui" };
  return { valid: false, type: "unknown" };
}

// ---------- Main ----------

export function validateInvoice(input: InvoiceValidationInput): ValidationIssue[] {
  const issues: ValidationIssue[] = [];

  // ----- Header -----
  if (isBlank(input.invoice_number)) {
    issues.push({
      field: "invoice_number",
      fieldLabel: "Număr factură",
      severity: "error",
      code: "INVOICE_NUMBER_REQUIRED",
      message: "Numărul facturii este obligatoriu (ex: EZF-001).",
    });
  }

  if (!isValidDate(input.issue_date)) {
    issues.push({
      field: "issue_date",
      fieldLabel: "Data emiterii",
      severity: "error",
      code: "ISSUE_DATE_INVALID",
      message: "Data emiterii este obligatorie și trebuie să fie validă.",
    });
  }

  // due_date is optional but if present must be >= issue_date
  if (input.due_date && isValidDate(input.due_date) && isValidDate(input.issue_date)) {
    if (input.due_date < (input.issue_date as string)) {
      issues.push({
        field: "due_date",
        fieldLabel: "Scadență",
        severity: "error",
        code: "DUE_DATE_BEFORE_ISSUE",
        message: "Scadența nu poate fi înainte de data emiterii.",
      });
    }
  }

  // ----- Seller -----
  if (isBlank(input.seller_name)) {
    issues.push({
      field: "seller_name",
      fieldLabel: "Furnizor — denumire",
      severity: "error",
      code: "SELLER_NAME_REQUIRED",
      message: "Denumirea furnizorului este obligatorie.",
    });
  }
  if (isBlank(input.seller_cui)) {
    issues.push({
      field: "seller_cui",
      fieldLabel: "Furnizor — CUI",
      severity: "error",
      code: "SELLER_CUI_REQUIRED",
      message: "CUI-ul furnizorului este obligatoriu.",
    });
  } else if (!validateCuiCnpInline(input.seller_cui as string).valid) {
    issues.push({
      field: "seller_cui",
      fieldLabel: "Furnizor — CUI",
      severity: "error",
      code: "SELLER_CUI_INVALID",
      message: "CUI furnizor invalid (max 10 cifre, opțional prefix RO; sau CNP 13 cifre).",
    });
  }
  if (isBlank(input.seller_address)) {
    issues.push({
      field: "seller_address",
      fieldLabel: "Furnizor — adresă",
      severity: "error",
      code: "SELLER_ADDRESS_REQUIRED",
      message: "Adresa furnizorului (stradă) este obligatorie.",
    });
  }
  if (isBlank(input.seller_city)) {
    issues.push({
      field: "seller_city",
      fieldLabel: "Furnizor — oraș",
      severity: "error",
      code: "SELLER_CITY_REQUIRED",
      message: "Orașul furnizorului este obligatoriu.",
    });
  }
  if (isBlank(input.seller_county)) {
    issues.push({
      field: "seller_county",
      fieldLabel: "Furnizor — județ",
      severity: "error",
      code: "SELLER_COUNTY_REQUIRED",
      message: "Județul furnizorului este obligatoriu.",
    });
  } else if (!COUNTY_CODES.has(input.seller_county as string)) {
    issues.push({
      field: "seller_county",
      fieldLabel: "Furnizor — județ",
      severity: "error",
      code: "SELLER_COUNTY_INVALID",
      message: "Cod județ furnizor invalid (trebuie ISO 3166-2:RO, ex: B, CJ, IS).",
    });
  }

  // ----- Buyer -----
  if (isBlank(input.buyer_name)) {
    issues.push({
      field: "buyer_name",
      fieldLabel: "Client — denumire",
      severity: "error",
      code: "BUYER_NAME_REQUIRED",
      message: "Denumirea clientului este obligatorie.",
    });
  }
  if (isBlank(input.buyer_cui)) {
    issues.push({
      field: "buyer_cui",
      fieldLabel: "Client — CUI",
      severity: "error",
      code: "BUYER_CUI_REQUIRED",
      message: "CUI-ul clientului este obligatoriu (sau CNP pentru persoane fizice).",
    });
  } else if (!validateCuiCnpInline(input.buyer_cui as string).valid) {
    issues.push({
      field: "buyer_cui",
      fieldLabel: "Client — CUI",
      severity: "error",
      code: "BUYER_CUI_INVALID",
      message: "CUI client invalid (max 10 cifre, opțional prefix RO; sau CNP 13 cifre).",
    });
  }
  if (isBlank(input.buyer_address)) {
    issues.push({
      field: "buyer_address",
      fieldLabel: "Client — adresă",
      severity: "error",
      code: "BUYER_ADDRESS_REQUIRED",
      message: "Adresa clientului (stradă) este obligatorie.",
    });
  }
  if (isBlank(input.buyer_city)) {
    issues.push({
      field: "buyer_city",
      fieldLabel: "Client — oraș",
      severity: "error",
      code: "BUYER_CITY_REQUIRED",
      message: "Orașul clientului este obligatoriu.",
    });
  }
  if (isBlank(input.buyer_county)) {
    issues.push({
      field: "buyer_county",
      fieldLabel: "Client — județ",
      severity: "error",
      code: "BUYER_COUNTY_REQUIRED",
      message: "Județul clientului este obligatoriu.",
    });
  } else if (!COUNTY_CODES.has(input.buyer_county as string)) {
    issues.push({
      field: "buyer_county",
      fieldLabel: "Client — județ",
      severity: "error",
      code: "BUYER_COUNTY_INVALID",
      message: "Cod județ client invalid (trebuie ISO 3166-2:RO, ex: B, CJ, IS).",
    });
  }

  // Cross-check: seller and buyer cannot be identical (would be a self-invoice)
  if (
    input.seller_cui &&
    input.buyer_cui &&
    !isBlank(input.seller_cui) &&
    !isBlank(input.buyer_cui)
  ) {
    const a = (input.seller_cui as string).replace(/^RO/i, "").trim();
    const b = (input.buyer_cui as string).replace(/^RO/i, "").trim();
    if (a && a === b) {
      issues.push({
        field: "buyer_cui",
        fieldLabel: "Client — CUI",
        severity: "error",
        code: "BUYER_EQUALS_SELLER",
        message: "CUI furnizor și CUI client nu pot fi identice.",
      });
    }
  }

  // ----- Line items -----
  if (!input.items || input.items.length === 0) {
    issues.push({
      field: "items",
      fieldLabel: "Produse / Servicii",
      severity: "error",
      code: "ITEMS_EMPTY",
      message: "Factura trebuie să conțină cel puțin un produs sau serviciu.",
    });
  } else {
    const nonEmpty = input.items.filter((i) => i.item_name && i.item_name.trim());
    if (nonEmpty.length === 0) {
      issues.push({
        field: "items",
        fieldLabel: "Produse / Servicii",
        severity: "error",
        code: "ITEMS_ALL_EMPTY",
        message: "Adaugă cel puțin un produs/serviciu cu denumire.",
      });
    }

    input.items.forEach((item, idx) => {
      const prefix = `items[${idx}]`;
      const label = `Linia ${idx + 1}`;

      if (isBlank(item.item_name)) {
        issues.push({
          field: `${prefix}.item_name`,
          fieldLabel: `${label} — denumire`,
          severity: "error",
          code: "ITEM_NAME_REQUIRED",
          message: `Linia ${idx + 1}: denumirea produsului este obligatorie.`,
        });
      }
      if (!Number.isFinite(item.quantity) || item.quantity <= 0) {
        issues.push({
          field: `${prefix}.quantity`,
          fieldLabel: `${label} — cantitate`,
          severity: "error",
          code: "ITEM_QUANTITY_INVALID",
          message: `Linia ${idx + 1}: cantitatea trebuie să fie strict pozitivă.`,
        });
      }
      if (!Number.isFinite(item.unit_price) || item.unit_price < 0) {
        issues.push({
          field: `${prefix}.unit_price`,
          fieldLabel: `${label} — preț unitar`,
          severity: "error",
          code: "ITEM_PRICE_INVALID",
          message: `Linia ${idx + 1}: prețul unitar trebuie să fie ≥ 0.`,
        });
      }
      if (!Number.isFinite(item.vat_rate) || item.vat_rate < 0) {
        issues.push({
          field: `${prefix}.vat_rate`,
          fieldLabel: `${label} — TVA %`,
          severity: "error",
          code: "ITEM_VAT_RATE_INVALID",
          message: `Linia ${idx + 1}: cota TVA trebuie să fie ≥ 0.`,
        });
      } else if (!ALLOWED_VAT_RATES.includes(item.vat_rate as (typeof ALLOWED_VAT_RATES)[number])) {
        issues.push({
          field: `${prefix}.vat_rate`,
          fieldLabel: `${label} — TVA %`,
          severity: "warning",
          code: "ITEM_VAT_RATE_NONSTANDARD",
          message: `Linia ${idx + 1}: cota TVA ${item.vat_rate}% nu este una standard în România (0, 5, 9, 11, 19, 21).`,
        });
      }
      if (!UNIT_CODE_SET.has(item.unit_code)) {
        issues.push({
          field: `${prefix}.unit_code`,
          fieldLabel: `${label} — U.M.`,
          severity: "error",
          code: "ITEM_UNIT_CODE_INVALID",
          message: `Linia ${idx + 1}: cod unitate de măsură invalid (UN/ECE Rec 20).`,
        });
      }
    });

    // ----- Totals consistency -----
    const computedSubtotal = input.items.reduce(
      (s, i) => s + (Number.isFinite(i.quantity) ? i.quantity : 0) * (Number.isFinite(i.unit_price) ? i.unit_price : 0),
      0,
    );
    const computedVat = input.items.reduce(
      (s, i) =>
        s +
        (Number.isFinite(i.quantity) ? i.quantity : 0) *
          (Number.isFinite(i.unit_price) ? i.unit_price : 0) *
          ((Number.isFinite(i.vat_rate) ? i.vat_rate : 0) / 100),
      0,
    );
    const computedTotal = computedSubtotal + computedVat;

    if (
      input.subtotal != null &&
      Math.abs((input.subtotal as number) - computedSubtotal) > MONEY_EPSILON
    ) {
      issues.push({
        field: "subtotal",
        fieldLabel: "Subtotal",
        severity: "error",
        code: "SUBTOTAL_MISMATCH",
        message: `Subtotalul stocat (${(input.subtotal as number).toFixed(2)}) nu corespunde sumei liniilor (${computedSubtotal.toFixed(2)}).`,
      });
    }
    if (
      input.vat_total != null &&
      Math.abs((input.vat_total as number) - computedVat) > MONEY_EPSILON
    ) {
      issues.push({
        field: "vat_total",
        fieldLabel: "TVA total",
        severity: "error",
        code: "VAT_TOTAL_MISMATCH",
        message: `TVA total stocat (${(input.vat_total as number).toFixed(2)}) nu corespunde sumei liniilor (${computedVat.toFixed(2)}).`,
      });
    }
    if (
      input.total != null &&
      Math.abs((input.total as number) - computedTotal) > MONEY_EPSILON
    ) {
      issues.push({
        field: "total",
        fieldLabel: "Total",
        severity: "error",
        code: "TOTAL_MISMATCH",
        message: `Totalul stocat (${(input.total as number).toFixed(2)}) nu corespunde subtotal + TVA (${computedTotal.toFixed(2)}).`,
      });
    }
  }

  return issues;
}

/** Convenience: only blocking errors (UI uses this to gate submit). */
export function getBlockingErrors(issues: ValidationIssue[]): ValidationIssue[] {
  return issues.filter((i) => i.severity === "error");
}
